export const STORAGE_KEY = 'responsecx_chat_history';
export const THREAD_ID_STORAGE_KEY = 'responsecx_thread_id';
export const SESSIONS_METADATA_KEY = 'responsecx_sessions_metadata';
export const CURRENT_SESSION_KEY = 'responsecx_current_session';
export const SESSION_DATA_PREFIX = 'responsecx_session_';

/** Org + agent a widget instance belongs to; used to namespace its storage keys. */
export interface StorageScope {
  orgId?: string | null;
  agentId?: string | null;
}

/**
 * `${base}:${orgId}:${agentId}` when both ids are known, else the legacy global
 * key, so two widgets for different agents on one origin stop sharing history.
 */
export function scopedStorageKey(base: string, scope?: StorageScope | null): string {
  return scope?.orgId && scope.agentId ? `${base}:${scope.orgId}:${scope.agentId}` : base;
}

/**
 * Reads `base` under `scope`. The first time the scoped key is empty while the
 * legacy key still holds data, the legacy value is moved over (copied, then the
 * legacy key removed) so an upgraded widget keeps its history and thread. The
 * move is one-time by design: once the scoped owner clears its key (a
 * conversation ended) the legacy data cannot come back, and the first scoped
 * reader on an origin claims it, so another org/agent widget never adopts it.
 * If the copy throws, the legacy key is left intact. Callers own try/catch.
 */
export function readScopedItem(
  storage: Storage,
  base: string,
  scope?: StorageScope | null
): string | null {
  const key = scopedStorageKey(base, scope);
  const value = storage.getItem(key);
  if (value !== null || key === base) return value;
  const legacy = storage.getItem(base);
  if (legacy !== null) {
    storage.setItem(key, legacy);
    storage.removeItem(base);
  }
  return legacy;
}
